/**
 * Scheduling API Client
 * API service for scheduling system (Calendly-like functionality)
 */

import { apiClient } from '../client';

export const schedulingApi = {
    // ============================================================================
    // EVENT TYPES
    // ============================================================================

    /**
     * Get all event types for a user
     */
    async getEventTypes(userId?: string) {
        const params = userId ? { userId } : {};
        return apiClient.get('/scheduling/event-types', { params });
    },

    /**
     * Get event type by ID
     */
    async getEventTypeById(id: string) {
        return apiClient.get(`/scheduling/event-types/${id}`);
    },

    /**
     * Get event type by slug (public)
     */
    async getEventTypeBySlug(slug: string) {
        return apiClient.get(`/scheduling/event-types/slug/${slug}`);
    },

    /**
     * Get event type by organization slug and event slug (public)
     */
    async getEventTypeByOrgAndSlug(orgSlug: string, eventSlug: string) {
        return apiClient.get(`/scheduling/event-types/org/${orgSlug}/${eventSlug}`);
    },

    /**
     * Create a new event type
     */
    async createEventType(data: {
        name: string;
        duration: number;
        description?: string;
        color?: string;
        custom_questions?: any[];
        max_invitees?: number;
        location_type?: string;
        location_details?: string;
        /** 'slots' (default) or 'webinar' (fixed-time group sessions) */
        event_kind?: 'slots' | 'webinar';
        /** Webinar only: fixed sessions [{ start_time ISO, end_time?, capacity? }] */
        sessions?: { id?: string; start_time: string; end_time?: string; capacity?: number | null; timezone?: string }[];
        /** Webinar only: registration fee in major units; null/0/absent = free */
        price?: number | null;
        currency?: string;
    }) {
        return apiClient.post('/scheduling/event-types', data);
    },

    /**
     * Update an event type
     */
    async updateEventType(id: string, data: Partial<{
        name: string;
        duration: number;
        description: string;
        color: string;
        active: boolean;
        custom_questions: any[];
        max_invitees: number;
        location_type: string;
        location_details: string;
        /** Webinar only: full session list (omitted sessions are cancelled/removed) */
        sessions: { id?: string; start_time: string; end_time?: string; capacity?: number | null; timezone?: string }[];
        /** Webinar only: registration fee in major units; null/0 = free */
        price: number | null;
        currency: string;
    }>) {
        return apiClient.put(`/scheduling/event-types/${id}`, data);
    },

    /**
     * Delete an event type.
     * Blocked with 409 EVENT_TYPE_HAS_ACTIVE_BOOKINGS while upcoming bookings
     * exist; pass { force: true } to cancel all upcoming bookings (invitees
     * are notified, Meet/Zoom meetings removed) and delete anyway.
     */
    async deleteEventType(id: string, options?: { force?: boolean }) {
        if (options?.force) {
            return apiClient.delete(`/scheduling/event-types/${id}?force=true`);
        }
        return apiClient.delete(`/scheduling/event-types/${id}`);
    },

    // ============================================================================
    // BOOKINGS
    // ============================================================================

    /**
     * Get all bookings
     */
    async getBookings(eventTypeId?: string) {
        const params = eventTypeId ? { eventTypeId } : {};
        return apiClient.get('/scheduling/bookings', { params });
    },

    /**
     * Get paginated bookings (meetings) with optional filtering
     */
    async getBookingsPaged(params: {
        eventTypeId?: string;
        limit?: number;
        offset?: number;
        timeFilter?: 'upcoming' | 'past' | 'all';
        status?: string;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
    } = {}) {
        return apiClient.get('/scheduling/bookings', {
            params: {
                ...params,
                search: params.search && params.search.trim().length > 0 ? params.search : undefined,
                status: params.status && params.status.trim().length > 0 ? params.status : undefined,
                dateFrom: params.dateFrom && params.dateFrom.trim().length > 0 ? params.dateFrom : undefined,
                dateTo: params.dateTo && params.dateTo.trim().length > 0 ? params.dateTo : undefined,
            }
        });
    },

    /**
     * Get booking by ID
     */
    async getBookingById(id: string) {
        return apiClient.get(`/scheduling/bookings/${id}`);
    },

    /**
     * Create a new booking
     */
    async createBooking(data: {
        event_type_id: string;
        /** Webinar registrations: the fixed session being registered for.
         *  Times are authoritative on the backend (taken from the session). */
        session_id?: string;
        start_time: string;
        end_time: string;
        invitee_name: string;
        invitee_email: string;
        invitee_phone: string;
        guest_emails?: string[];
        location_type?: string;
        location_details?: string;
        notes?: string;
        timezone?: string;
        answers?: any[];
        whatsapp_reminder_enabled?: boolean;
    }) {
        return apiClient.post('/scheduling/bookings', data);
    },

    /**
     * "Notify host" — customer-triggered recovery path shown when a booking
     * failed because of a host-side integration problem (e.g. Zoom
     * disconnected). Public endpoint, mirrors createBooking.
     */
    async notifyBookingFailure(data: {
        event_type_id: string;
        requested_start_time?: string;
        invitee_email: string;
        invitee_name?: string;
        invitee_phone?: string;
    }) {
        return apiClient.post('/scheduling/bookings/notify-failure', data);
    },

    /**
     * List scheduling contacts (unique invitees + last meeting date)
     */
    async getSchedulingContacts(params: { limit?: number; offset?: number; search?: string } = {}) {
        return apiClient.get('/scheduling/contacts', {
            params: {
                ...params,
                search: params.search && params.search.trim().length > 0 ? params.search : undefined,
            }
        });
    },

    /**
     * Cancel a booking
     */
    async cancelBooking(id: string, cancellation_reason?: string) {
        return apiClient.post(`/scheduling/bookings/${id}/cancel`, { cancellation_reason });
    },

    /**
     * Reschedule a booking
     */
    async rescheduleBooking(id: string, data: {
        start_time: string;
        end_time: string;
    }) {
        return apiClient.post(`/scheduling/bookings/${id}/reschedule`, data);
    },

    // ============================================================================
    // AVAILABILITY
    // ============================================================================

    /**
     * Get available time slots for an event type
     */
    async getAvailableSlots(eventTypeId: string, startDate: string, endDate: string) {
        return apiClient.get(`/scheduling/availability/${eventTypeId}`, {
            params: { startDate, endDate }
        });
    },

    // ============================================================================
    // AVAILABILITY SETTINGS
    // ============================================================================

    /**
     * Get availability settings for a user
     */
    async getAvailabilitySettings(userId?: string, eventTypeId?: string) {
        const params: any = {};
        if (userId) params.userId = userId;
        if (eventTypeId) params.eventTypeId = eventTypeId;
        return apiClient.get('/scheduling/availability-settings', { params });
    },

    /**
     * Get default availability for a user
     */
    async getDefaultAvailability(userId?: string) {
        const params = userId ? { userId } : {};
        return apiClient.get('/scheduling/availability-settings/default', { params });
    },

    /**
     * Create availability setting
     */
    async createAvailabilitySetting(data: {
        user_id?: string;
        event_type_id?: string | null;
        timezone?: string;
        slot_duration?: number;
        monday_start?: number | null;
        monday_end?: number | null;
        tuesday_start?: number | null;
        tuesday_end?: number | null;
        wednesday_start?: number | null;
        wednesday_end?: number | null;
        thursday_start?: number | null;
        thursday_end?: number | null;
        friday_start?: number | null;
        friday_end?: number | null;
        saturday_start?: number | null;
        saturday_end?: number | null;
        sunday_start?: number | null;
        sunday_end?: number | null;
        // Optional recurring break (e.g. lunch), minutes-from-midnight (0-1439).
        // Either both are set or neither is.
        break_start?: number | null;
        break_end?: number | null;
    }) {
        return apiClient.post('/scheduling/availability-settings', data);
    },

    /**
     * Update availability setting
     */
    async updateAvailabilitySetting(id: string, data: any) {
        return apiClient.put(`/scheduling/availability-settings/${id}`, data);
    },

    /**
     * Delete availability setting
     */
    async deleteAvailabilitySetting(id: string) {
        return apiClient.delete(`/scheduling/availability-settings/${id}`);
    },

    /**
     * Get available slots for a specific date
     */
    async getAvailableSlotsForDate(eventTypeId: string, date: string) {
        return apiClient.get(`/scheduling/availability/${eventTypeId}`, {
            params: { startDate: date, endDate: date }
        });
    },

    /**
     * Get available slots for a date range
     */
    async getAvailableSlotsRange(eventTypeId: string, startDate: string, endDate: string) {
        return apiClient.get(`/scheduling/availability/${eventTypeId}/range`, {
            params: { startDate, endDate }
        });
    },

    // ============================================================================
    // INTEGRATIONS
    // ============================================================================

    async getIntegrationStatus(userId?: string) {
        const params = userId ? { userId } : {};
        return apiClient.get('/scheduling/integrations/status', { params });
    },

    async toggleIntegration(provider: 'google' | 'microsoft' | 'zoom' | 'google_sheets' | 'google_calendar', status: boolean, userId?: string) {
        return apiClient.post('/scheduling/integrations/toggle', { provider, status, userId });
    },

    // Whether the org has a payment gateway connected to collect paid webinar
    // registration fees. Shape: { success, data: { connected, provider } }.
    async getPaymentGatewayStatus() {
        return apiClient.get('/scheduling/payment-gateway-status');
    },

    async getSchedulingUsers() {
        return apiClient.get('/scheduling/users');
    },

    /**
     * Get current user's profile and organization info
     */
    async getCurrentUser() {
        return apiClient.get('/scheduling/me');
    },
};
